import type { ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/supabase/client';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { checkAdminStatus } from '@/helpers/auth/checkAdminStatus';
import { AuthContext } from './AuthContext';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { authProviderMessages } from './authProvider.messages';
import { ROUTES } from '@/routes';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle auth state changes
  const handleAuthStateChange = useCallback(
    (event: AuthChangeEvent, newSession: Session | null) => {
      setSession(newSession);

      if (newSession?.user) {
        // Check admin status for new sessions
        checkAdminStatus(newSession.user)
          .then((adminStatus) => {
            setIsAdmin(adminStatus);
          })
          .catch(() => {
            setIsAdmin(false);
          })
          .finally(() => {
            setAuthLoading(false);
          });
      } else {
        setIsAdmin(false);
        setAuthLoading(false);

        // Redirect to waitlist on initial load if no session and not already on waitlist
        // but still allow users to directly access the login page
        if (
          event === 'INITIAL_SESSION' &&
          location.pathname !== ROUTES.WAITLIST &&
          location.pathname !== ROUTES.LOGIN
        ) {
          navigate(ROUTES.WAITLIST, { replace: true });
        }
      }
    },
    [navigate, location.pathname]
  );

  // Handle sign out with redirect
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      // Navigation will be handled by auth state change listener
    } catch {
      toast.error(authProviderMessages.signOutError);
    }
  }, []);

  useLoadOnce(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        handleAuthStateChange('INITIAL_SESSION', initialSession);
      } catch {
        toast.error(authProviderMessages.initialSessionError);
        setAuthLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    return () => {
      subscription.unsubscribe();
    };
  });

  const value = useMemo(() => {
    return {
      session,
      isAdmin,
      authLoading,
      signOut,
    };
  }, [session, isAdmin, authLoading, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
